import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	currentTargetRequired,
	getAttributeOrThrow,
	getAttributeOrUndefined,
	openWaitlistModal,
	PUB_SUB_EVENTS,
	publish,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import type { ProductVariant } from '@/scripts/types/api'
import {
	addItemsToCart,
	displayDataFromProductVariant,
	getCartUpsell,
	getDOMCart,
	getDOMCartSectionApiIds,
	getSectionHTMLForResource,
	renderResponseToCartDrawer,
	ShopifyProductOptions,
	variantFromProductOptions,
} from '@/scripts/core/cart-functions'

export class ProductCard extends UcoastEl {
	static htmlSelector = 'product-card'
	variantData?: ProductVariant[]
	productUrl: string
	dynamicContentContainer?: HTMLElement
	uniqueId: string
	initialized: boolean = false
	hasLoadedMedia = false
	tryUpsell = false
	elements?: {
		form: HTMLFormElement
		submitButton: HTMLButtonElement
		variantIdInput: HTMLInputElement
		dynamicText: {
			priceTextEl: HTMLElement
			actionTextEl: HTMLElement
			dividerEl?: HTMLElement
		}
		options?: NodeListOf<HTMLInputElement>
	}

	constructor() {
		super()
		this.productUrl = getAttributeOrThrow('data-product-url', this)
		this.dynamicContentContainer = qsOptional(
			'[data-dynamic-options]',
			this
		)
		this.uniqueId = getAttributeOrThrow('data-unique-id', this)
		if (!this.dynamicContentContainer) {
			// if the form is statically loaded, we need to initialize variantData outside the async flow

			void this.initializeForm()
		}
		this.addEventListener('mouseenter', () => {
			if (this.hasLoadedMedia) return

			void window.Ucoast.mediaManager.loadAllInContainer(this)
			this.hasLoadedMedia = true
		})
		this.addEventListener('touchstart', () => {
			if (this.hasLoadedMedia) return

			void window.Ucoast.mediaManager.loadAllInContainer(this)
			this.hasLoadedMedia = true
		})

	}

	getElements() {
		return {
			form: qsRequired<HTMLFormElement>('form', this),
			options: qsaOptional<HTMLInputElement>('input[data-option]', this),
			submitButton: qsRequired<HTMLButtonElement>(
				'button[type="submit"]',
				this
			),
			variantIdInput: qsRequired<HTMLInputElement>(
				'input[data-variant-id]',
				this
			),
			dynamicText: {
				priceTextEl: qsRequired<HTMLElement>(
					'[data-dynamic="price"]',
					this
				),
				actionTextEl: qsRequired<HTMLElement>(
					'[data-dynamic="action"]',
					this
				),
				dividerEl: qsOptional<HTMLElement>(
					'[data-dynamic="divider"]',
					this
				),
			},
		}
	}

	setElements() {
		const submitButton = qsOptional<HTMLButtonElement>(
			'button[type="submit"]',
			this
		)
		if (!submitButton) {
			this.elements = undefined
			return
		}
		this.elements = this.getElements()
	}

	async renderDynamicContent() {
		if (!this.dynamicContentContainer) return
		const content = await getSectionHTMLForResource(
			this.productUrl,
			'dynamic-product-form'
		)
		if (!content) return
		const contentWithUniqueId = content.replaceAll(
			'_dynamic_section_id_',
			this.uniqueId
		)
		this.dynamicContentContainer.outerHTML = contentWithUniqueId
		window.setTimeout(() => {
			this.classList.add('loaded')
		}, 5)
	}

	override connectedCallback() {
		super.connectedCallback()
	}

	override animateIn() {
		super.animateIn()
		void this.initializeForm()
	}

	override getObserverOptions() {
		return {
			root: null,
			rootMargin: '500px 0px 500px 0px',
			threshold: this.getThreshold(),
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.cleanup()
	}

	updateSelectedVariant() {
		const elements = this?.elements
		if (!elements) throw new Error('cant update variant, no elements found')
		if (!this.variantData)
			throw new Error('cant update variant, no variant data found')
		const options = this.getOptionValues()
		const variant = variantFromProductOptions(options, this.variantData)
		const displayData = displayDataFromProductVariant(variant)
		elements.variantIdInput.value = variant.id.toString()
		elements.dynamicText.priceTextEl.innerHTML =
			displayData.locale_price_without_trailing_zeros
		elements.dynamicText.actionTextEl.innerHTML =
			displayData.inventory_status_text
		if (displayData.inventory_status === 'OutOfStock') {
			elements.submitButton.setAttribute('data-oos', 'true')
		} else {
			elements.submitButton.removeAttribute('data-oos')
		}
	}

	getOptionValues(): ShopifyProductOptions {
		if (!this.elements?.options) {
			throw new Error('No options found')
		}
		const option1Inputs = qsaRequired<HTMLInputElement>(
			'input[data-option="option1"]',
			this
		)
		const option1 = Array.from(option1Inputs).find((input) => input.checked)
			?.value
		if (!option1) {
			throw new Error('No option1 found')
		}
		const option2Inputs = qsaOptional<HTMLInputElement>(
			'input[data-option="option2"]',
			this
		)
		const option2 = Array.from(option2Inputs ?? []).find(
			(input) => input.checked
		)?.value
		const option3Inputs = qsaOptional<HTMLInputElement>(
			'input[data-option="option3"]',
			this
		)
		const option3 = Array.from(option3Inputs ?? []).find(
			(input) => input.checked
		)?.value
		return {
			option1,
			option2,
			option3,
		}
	}

	setListeners() {
		const elements = this.elements
		if (!elements) {
			return
		}
		elements?.options?.forEach((option) => {
			option.addEventListener('change', () => {
				this.updateSelectedVariant()
			})
		})
		elements.form.addEventListener('submit', (event) => {
			event.preventDefault()
			const variantId = parseInt(
				getAttributeOrThrow('value', elements.variantIdInput)
			)
			if (elements.submitButton.hasAttribute('data-oos')) {
				openWaitlistModal(variantId, currentTargetRequired(event))
				return
			}

			// block normal flow and follow upsell path
			if (this.hasAttribute('data-try-upsell')) {
				void addItemsToCart(
					[
						{
							id: variantId,
							quantity: 1,
							properties: {},
						},
					],
					getDOMCartSectionApiIds()
				)
					.then(async (cart) => {
						if ('status' in cart) {
							return
						}

						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: 'product-form',
							productVariantId: variantId,
						})

						// Update cart drawer + icon bubble from the /cart/add response
						if ('sections' in cart) {
							try {
								getDOMCart()?.renderContentsWithoutOpening(cart)
							} catch (e) {
								console.error('Failed to render cart contents', e)
							}
						}

						// Fetch and show upsell from the product URL (needs product context)
						const upsell = getCartUpsell()
						if (upsell) {
							try {
								await upsell.reloadAndOpen(this.productUrl, this.elements?.submitButton)
							} catch (e) {
								console.error('Failed to show upsell, opening cart drawer', e)
								getDOMCart()?.open()
							}
						} else {
							getDOMCart()?.open()
						}
					})
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {})
				return
			}

			// standard add to cart
			void addItemsToCart(
				[
					{
						id: variantId,
						quantity: 1,
						properties: {},
					},
				],
				getDOMCartSectionApiIds()
			)
				.then((cart) => {
					if ('status' in cart) {
						return
					}

					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: 'product-form',
						productVariantId: variantId,
					})
					renderResponseToCartDrawer(cart)
				})
				.catch((e) => {
					console.error(e)
				})
				.finally(() => {})
		})
	}

	async initializeForm() {
		if (this.initialized) return
		void (await this.renderDynamicContent())
		this.variantData = this.getVariantData()
		this.setElements()
		this.setListeners()
		this.initialized = true
		this.tryUpsell = !!getAttributeOrUndefined('data-try-upsell', this)
	}
	cleanup() {}

	getVariantData(): ProductVariant[] {
		const textContent = qsRequired<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		return JSON.parse(textContent)
	}
}
